/**
 * Áreas del árbol organizacional.
 *
 * ⚠️ Nombre LEGACY: la entidad se llama `Area` y las rutas son `/api/areas`.
 *
 * Árbol sede → facultad → programa SIN límite de profundidad. Es un bosque:
 * admite varias raíces, una por sede.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type { Programa } from '@/interfaces/Programa'
import { getProgramasService, getArbolAreasService, getProgramaByIdService, crearProgramaService, updateProgramaService, reordenarAreaService, eliminarProgramaService, asignarCampusAreaService, desasignarCampusAreaService
} from '@/services/ProgramaService'

/** Store `programas`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionProgramas = defineStore('programas', {
  state: () => ({
    programas: [] as Programa[],
    arbol: [] as Programa[],
    programaActual: null as Programa | null
  }),

  actions: {
    // Lista plana de áreas (con filtro opcional por campus)
    async cargarTodos(idCampus?: number | null): Promise<void> {
      this.programas = await getProgramasService(idCampus)
    },

    // Árbol organizacional con hijos_recursivos (con filtro opcional por campus)
    async cargarArbol(idCampus?: number | null): Promise<void> {
      this.arbol = await getArbolAreasService(idCampus)
    },

    async cargarPorId(id: number): Promise<Programa | null> {
      const programa = await getProgramaByIdService(id)
      this.programaActual = programa
      return programa
    },

    async crear(
      data: Omit<Programa, 'id_programa' | 'ordinal' | 'hijos_recursivos'>
    ): Promise<{ ok: boolean; data?: Programa; errors?: Record<string, string[]> }> {
      const result = await crearProgramaService(data)
      if (result.ok && result.data) {
        this.programas.push(result.data)
        this.programaActual = result.data
      }
      return result
    },

    async actualizar(
      id: number,
      payload: Partial<Programa>
    ): Promise<{ ok: boolean; data?: Programa; errors?: Record<string, string[]> }> {
      const result = await updateProgramaService(id, payload)
      if (result.ok && result.data) {
        const idx = this.programas.findIndex(p => p.id_programa === id)
        if (idx !== -1) this.programas.splice(idx, 1, result.data)
        this.programaActual = result.data
      }
      return result
    },

    // Reposicionar área entre sus hermanas (C-12)
    async reordenar(
      id: number,
      posicion: number
    ): Promise<{ ok: boolean; message?: string }> {
      return await reordenarAreaService(id, posicion)
    },

    async asignarCampus(
      idArea: number,
      idCampus: number
    ): Promise<{ ok: boolean; data?: Programa; errors?: Record<string, string[]> }> {
      const result = await asignarCampusAreaService(idArea, idCampus)
      if (result.ok && result.data) {
        const idx = this.programas.findIndex(p => p.id_programa === idArea)
        if (idx !== -1) this.programas.splice(idx, 1, result.data)
      }
      return result
    },

    async desasignarCampus(
      idArea: number,
      idCampus: number
    ): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
      const result = await desasignarCampusAreaService(idArea, idCampus)
      if (result.ok) {
        const area = this.programas.find(p => p.id_programa === idArea)
        if (area?.campus) {
          area.campus = area.campus.filter(c => c.id_campus !== idCampus)
        }
      }
      return result
    },

    async eliminar(id: number): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
      const result = await eliminarProgramaService(id)
      if (result.ok) {
        this.programas = this.programas.filter(p => p.id_programa !== id)
        if (this.programaActual?.id_programa === id) this.programaActual = null
      }
      return result
    }
  }
})
